/**
 * <parlay-lens-upsell>
 * --------------------
 * Post-add-to-cart "Complete your Day/Night Set" modal for Parlay eyewear.
 *
 * When a frame is added, we resolve its OPPOSITE lens counterpart (Everyday <->
 * Dusk) server-side (metafields aren't exposed on /products/{handle}.js) by
 * fetching /products/{added-handle}?view=opposite-lens. That fragment renders a
 * single offer card plus, inside a collapsed list, every other frame in the
 * opposite lens's collection.
 *
 * The shopper can swap the suggested frame ("Choose different frame" -> Select),
 * then one big "Complete bundle" button adds the selected frame and goes to cart.
 * A small "No thanks" link just goes to cart. Bundle pricing (a struck-through
 * "would have cost" from both frames' original prices, the editable set price,
 * and the savings) is computed here and updates when the frame changes.
 *
 * The cart drawer stays usable via the cart icon; we only suppress its auto-open
 * for the triggering add so just the popup shows.
 */
if (!customElements.get('parlay-lens-upsell')) {
  customElements.define(
    'parlay-lens-upsell',
    class ParlayLensUpsell extends HTMLElement {
      connectedCallback() {
        this.matchType = (this.dataset.matchType || '').trim().toLowerCase();
        this.offerView = (this.dataset.offerView || 'opposite-lens').trim();
        this.bundlePrice = parseInt(this.dataset.bundlePrice, 10) || 0; // cents
        // Pair ladder: ladder[n-1] = total cents for n pairs; past the end, the last per-pair rate repeats
        try { this.ladder = JSON.parse(this.dataset.ladder || 'null'); } catch (e) { this.ladder = null; }
        this.pairsInCart = 1;
        this._inflight = new Map();
        this.addedOriginal = 0; // cents; compare-at of the added frame (drives "You save")
        this.addedPrice = 0; // cents; ACTUAL price of the added frame (drives the receipt)

        this.onProductAdded = this.onProductAdded.bind(this);
        this.onClick = this.onClick.bind(this);

        document.addEventListener('ajaxProduct:added', this.onProductAdded);

        // The x-modal relocates itself to <body> when it opens (shouldAppendToBody),
        // taking its buttons with it. Listen on the modal, which travels with them —
        // NOT on this host, whose subtree the modal leaves. Same reason every content
        // lookup goes through this.root below.
        this.modal?.addEventListener('click', this.onClick);

        this.modal?.addEventListener('modal:afterHide', () => {
          this._suppress = false;
          this.cartDrawer?.removeAttribute('data-suppress-auto-open');
        });
      }

      disconnectedCallback() {
        document.removeEventListener('ajaxProduct:added', this.onProductAdded);
      }

      /* ------------------------------------------------------------- Getters */

      get modal() {
        return this._modal || (this._modal = this.querySelector('x-modal'));
      }
      // The modal (and everything inside it) moves to <body> on open, so scope all
      // content lookups to the modal, not to this host element.
      get root() {
        return this.modal || this;
      }
      get grid() {
        return this.root.querySelector('[data-parlay-lens-grid]');
      }
      get selectedCard() {
        return this.root.querySelector('[data-parlay-selected]');
      }
      get errorEl() {
        return this.root.querySelector('[data-parlay-lens-error]');
      }
      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      /* ---------------------------------------------------------------- Trigger */

      async onProductAdded(event) {
        if (document.body.classList.contains('template-cart')) return;

        const item = event?.detail?.product;
        if (!item || !item.handle) return;
        if (this.modal && this.modal.contains(event.target)) return;

        if (this.matchType) {
          const type = (item.product_type || '').trim().toLowerCase();
          if (type !== this.matchType) return;
        }

        // Suppress the drawer's auto-open now (it fires right after this event);
        // released if there turns out to be no offer.
        this.suppressCartDrawer();

        let source;
        try {
          const html = await this.fetchOffer(item.handle);
          source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-parlay-lens-offer-source]');
        } catch (e) {
          this.releaseCartDrawer();
          return;
        }

        const card = source && source.querySelector('[data-parlay-selected]');
        if (!card) {
          // No counterpart / out of stock / already in cart -> normal drawer.
          this.releaseCartDrawer();
          return;
        }

        this.addedOriginal = parseInt(source.dataset.addedOriginal, 10) || 0;
        this.addedPrice = parseInt(source.dataset.addedPrice, 10) || 0;
        this.pairsInCart = await this.countPairs(item.product_type);
        this.grid.replaceChildren(...source.children);
        this.setError(false);
        this.updatePricing();
        this.open();
      }

      // One shared in-flight fetch per handle (dedupes a rapid double-add). Cleared
      // on settle so a later add re-fetches with fresh cart/offer state.
      fetchOffer(handle) {
        if (this._inflight.has(handle)) return this._inflight.get(handle);
        const url = `/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(this.offerView)}`;
        const promise = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`opposite-lens fetch failed (${response.status})`);
            return response.text();
          })
          .finally(() => this._inflight.delete(handle));
        this._inflight.set(handle, promise);
        return promise;
      }

      // Pairs already in the cart (the one just added included), so the offer prices the NEXT pair
      async countPairs(type) {
        try {
          const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
          const want = (type || this.matchType || '').trim().toLowerCase();
          const n = cart.items
            .filter((i) => (i.product_type || '').trim().toLowerCase() === want && i.final_line_price > 0)
            .reduce((sum, i) => sum + i.quantity, 0);
          return Math.max(1, n);
        } catch (e) {
          return 1;
        }
      }

      total(n) {
        const l = this.ladder;
        if (Array.isArray(l) && l.length) {
          if (n <= l.length) return l[n - 1];
          return l[l.length - 1] + Math.round(((n - l.length) * l[l.length - 1]) / l.length);
        }
        return n <= 1 ? this.addedPrice : this.bundlePrice + (n - 2) * (this.bundlePrice - this.addedPrice);
      }

      /* ---------------------------------------------------------- Interactions */

      onClick(event) {
        if (event.target.closest('[data-parlay-choose]')) {
          this.toggleAlternatives();
          return;
        }
        const alt = event.target.closest('[data-parlay-select]') && event.target.closest('[data-parlay-alt]');
        if (alt) {
          this.selectFrame(alt);
          return;
        }
        if (event.target.closest('[data-parlay-complete]')) {
          this.completeBundle();
          return;
        }
        if (event.target.closest('[data-parlay-nothanks]')) {
          this.goToCart();
        }
      }

      toggleAlternatives() {
        const btn = this.root.querySelector('[data-parlay-choose]');
        const alts = this.root.querySelector('[data-parlay-alts]');
        if (!btn || !alts) return;
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        alts.toggleAttribute('hidden', open);
      }

      selectFrame(altEl) {
        const card = this.selectedCard;
        if (!card) return;

        card.dataset.variantId = altEl.dataset.variantId || card.dataset.variantId;
        card.dataset.original = altEl.dataset.original || card.dataset.original;
        card.dataset.price = altEl.dataset.price || card.dataset.price;

        const img = card.querySelector('[data-parlay-sel-image]');
        const title = card.querySelector('[data-parlay-sel-title]');
        if (img && altEl.dataset.image) img.src = altEl.dataset.image;
        if (title && altEl.dataset.title) title.textContent = altEl.dataset.title;

        this.root.querySelectorAll('[data-parlay-alt]').forEach((a) => {
          const isSel = a === altEl;
          a.classList.toggle('is-selected', isSel);
          const btn = a.querySelector('[data-parlay-select]');
          if (btn) btn.textContent = isSel ? 'Selected' : 'Select';
        });
        this.updatePricing();
      }

      /* -------------------------------------------------------------- Pricing */

      updatePricing() {
        const card = this.selectedCard;
        if (!card) return;

        // --- Deal receipt (ACTUAL prices) ----------------------------------
        // "In cart" = the pairs already there at their ladder total. The offered pair
        // costs what it adds to the ladder (e.g. 1 in cart: $114 - $74 = $40; 2 in cart:
        // $159 - $114 = $45). Struck "was" is the offered frame's own price.
        const q = this.pairsInCart || 1;
        const setTotal = this.total(q + 1);
        const firstPair = this.total(q);
        const secondWas = parseInt(card.dataset.price, 10) || 0;
        const secondNow = setTotal - firstPair;
        const words = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth'];
        const firstLabel = card.querySelector('[data-parlay-first-label]');
        const secondLabel = card.querySelector('[data-parlay-second-label]');
        const totalLabel = card.querySelector('[data-parlay-total-label]');
        if (firstLabel) firstLabel.textContent = q === 1 ? 'First pair' : `${q} pairs`;
        if (secondLabel) secondLabel.textContent = `${words[q] || 'Next'} pair`;
        if (totalLabel) totalLabel.textContent = q === 1 ? 'Set total' : 'New total';
        const receipt = card.querySelector('[data-parlay-receipt]');
        const validReceipt = firstPair > 0 && secondNow > 0;
        if (receipt) receipt.toggleAttribute('hidden', !validReceipt);
        if (validReceipt) {
          const firstEl = card.querySelector('[data-parlay-first-price]');
          const wasEl = card.querySelector('[data-parlay-second-was]');
          const nowEl = card.querySelector('[data-parlay-second-now]');
          const totalEl = card.querySelector('[data-parlay-set-total]');
          if (firstEl) firstEl.textContent = this.money(firstPair);
          if (nowEl) nowEl.textContent = this.money(secondNow);
          if (totalEl) totalEl.textContent = this.money(setTotal);
          if (wasEl) {
            // Strike the second frame's normal price only when the set beats it.
            const showWas = secondWas > secondNow;
            wasEl.textContent = showWas ? this.money(secondWas) : '';
            wasEl.toggleAttribute('hidden', !showWas);
          }
        }

        // --- "You save" vs. MSRP (COMPARE-AT prices) — separate channel -----
        const selectedOriginal = parseInt(card.dataset.original, 10) || 0;
        const combined = this.addedOriginal * q + selectedOriginal;
        const savings = Math.max(0, combined - setTotal);

        // Struck compare-at ("would have cost") on the set total, same MSRP channel.
        const setWasEl = card.querySelector('[data-parlay-set-was]');
        if (setWasEl) {
          const showSetWas = combined > setTotal;
          setWasEl.textContent = showSetWas ? this.money(combined) : '';
          setWasEl.toggleAttribute('hidden', !showSetWas);
        }

        const savingsWrap = this.root.querySelector('[data-parlay-savings]');
        const savingsAmt = this.root.querySelector('[data-parlay-savings-amount]');
        if (savingsWrap && savingsAmt) {
          savingsAmt.textContent = this.money(savings);
          savingsWrap.toggleAttribute('hidden', savings <= 0);
        }
      }

      money(cents) {
        return '$' + (Math.round(cents) / 100).toFixed(2);
      }

      /* ------------------------------------------------------------- Open/close */

      open() {
        this.suppressCartDrawer();
        if (this.modal && typeof this.modal.show === 'function') {
          this.modal.show();
        } else if (this.modal) {
          this.modal.setAttribute('open', '');
        }
      }

      suppressCartDrawer() {
        this._suppress = true;
        const drawer = this.cartDrawer;
        if (!drawer) return;
        drawer.setAttribute('data-suppress-auto-open', '');
        if (drawer.open && typeof drawer.hide === 'function') drawer.hide();
      }

      releaseCartDrawer() {
        this._suppress = false;
        const drawer = this.cartDrawer;
        if (!drawer) return;
        drawer.removeAttribute('data-suppress-auto-open');
        if (typeof drawer.show === 'function' && !drawer.open) drawer.show();
      }

      goToCart() {
        this._suppress = false;
        this.cartDrawer?.removeAttribute('data-suppress-auto-open');
        this.modal?.hide?.();
        const drawer = this.cartDrawer;
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else {
          window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
        }
      }

      /* --------------------------------------------------------- Complete bundle */

      completeBundle() {
        const card = this.selectedCard;
        const variantId = card && card.dataset.variantId;
        if (!variantId) return;
        const button = this.root.querySelector('[data-parlay-complete]');
        this.addItem(variantId, button, () => this.goToCart());
      }

      addItem(variantId, button, onDone) {
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
        }
        this.setError(false);

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsed) => {
            if (parsed.status) {
              this.setError(parsed.description || parsed.message);
              return;
            }

            const cartJson = await (
              await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
            ).json();
            cartJson.sections = parsed.sections;

            // Refresh the cart drawer + any progress UI. We deliberately do NOT
            // re-dispatch ajaxProduct:added (that would re-trigger this popup).
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'parlay-lens-upsell',
              cart: cartJson,
            });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartJson } }));

            if (typeof onDone === 'function') onDone();
          })
          .catch(() => {
            this.setError(true);
          })
          .finally(() => {
            if (button) {
              button.removeAttribute('aria-busy');
              button.removeAttribute('aria-disabled');
            }
          });
      }

      setError(message) {
        const el = this.errorEl;
        if (!el) return;
        el.toggleAttribute('hidden', !message);
        el.textContent = typeof message === 'string' ? message : '';
      }
    }
  );
}
